import React, { useState, useMemo, useEffect } from 'react';
import {
  useGenererLotCopiesMutation,
  useGetLotsCopiesQuery,
  useLazyExportLotExcelQuery,
  useGetStatistiquesGlobalesQuery,
  useGetProchainCodeQuery,
  useGetStatistiquesLotQuery,
  useDeleteLotCopiesMutation,
  useGetModelesCopiesQuery,
  useGenererPdfLotsMutation,
  useMarquerLotImprimeMutation,
  useMarquerLotNonImprimeMutation,
  useMarquerLotUtiliseMutation,
  useMarquerLotNonUtiliseMutation,
} from '../../services/copiesApi';
import {
  useGetSessionsQuery,
  useLazyGetEstimationCopiesQuery,
  EstimationCopiesResponse,
} from '../../services/emploisTempsApi';
import {
  Printer,
  Download,
  Barcode,
  CheckCircle2,
  XCircle,
  TrendingUp,
  Users,
  FileText,
  Calendar,
  Search,
  ChevronDown,
  ChevronUp,
  BarChart2,
  RefreshCw,
  Award,
  AlertCircle,
  FileImage,
  Trash2,
  Calculator,
  Loader2,
  FileDown,
  Package,
} from 'lucide-react';
import { Spinner } from '../../components/ui/Spinner';
import { RepartitionMini } from '../../components/ui/RepartitionMini';
import ImpressionCopies from './ImpressionCopies';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function ProgressBar({ value, className = '' }: { value: number; className?: string }) {
  const color =
    value >= 100
      ? 'bg-green-500'
      : value >= 50
      ? 'bg-yellow-500'
      : 'bg-red-400';
  return (
    <div className={`relative h-1.5 rounded-full bg-gray-200 dark:bg-gray-700 overflow-hidden ${className}`}>
      <div
        className={`absolute inset-y-0 left-0 rounded-full transition-all duration-300 ${color}`}
        style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
      />
    </div>
  );
}

function TauxBadge({ value }: { value: number }) {
  const v = Number(value || 0);
  if (v >= 100)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
        <CheckCircle2 size={10} />
        100%
      </span>
    );
  if (v >= 50)
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300">
        {v.toFixed(1)}%
      </span>
    );
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
      {v.toFixed(1)}%
    </span>
  );
}

function TypeBadge({ type }: { type: string }) {
  switch (type) {
    case 'NORMAL':
      return (
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
          Normal
        </span>
      );
    case 'RATTRAPAGE':
      return (
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300">
          Rattrapage
        </span>
      );
    case 'DEVOIR':
      return (
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
          Devoir
        </span>
      );
    default:
      return (
        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
          {type}
        </span>
      );
  }
}

/** Fetches and displays per-lot statistics when a row is expanded. */
function LotDetailRow({ lotId }: { lotId: number }) {
  const { data: stats, isLoading } = useGetStatistiquesLotQuery(lotId);

  if (isLoading)
    return (
      <div className="flex justify-center py-4">
        <Spinner size="sm" />
      </div>
    );

  if (!stats)
    return (
      <p className="py-4 text-center text-sm text-gray-500 dark:text-gray-400">
        Statistiques non disponibles
      </p>
    );

  const items = [
    {
      label: 'Copies associées',
      value: stats.copies_associees,
      rate: stats.taux_association,
      color: 'text-blue-600 dark:text-blue-400',
    },
    {
      label: 'Non associées',
      value: stats.copies_non_associees,
      rate: null,
      color: 'text-red-500 dark:text-red-400',
    },
    {
      label: 'Copies notées',
      value: stats.copies_notees,
      rate: stats.taux_notation,
      color: 'text-green-600 dark:text-green-400',
    },
    {
      label: 'Non notées',
      value: stats.copies_non_notees,
      rate: null,
      color: 'text-gray-500 dark:text-gray-400',
    },
  ];

  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 px-6 py-4 bg-gray-50 dark:bg-gray-800/60 border-t border-gray-100 dark:border-gray-700">
      {items.map((item) => (
        <div key={item.label} className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{item.label}</p>
          <p className={`text-xl font-bold ${item.color}`}>{item.value}</p>
          {item.rate !== null && (
            <>
              <ProgressBar value={item.rate} className="mt-1.5" />
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                {item.rate.toFixed(1)}%
              </p>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Types ───────────────────────────────────────────────────────────────────

type TypeFilter = 'ALL' | 'NORMAL' | 'RATTRAPAGE' | 'DEVOIR';
type StatusFilter = 'ALL' | 'complet' | 'partiel' | 'vide';
type GroupBy = 'none' | 'session';

const TYPE_COLORS: Record<string, string> = {
  'ordinaire': 'bg-blue-600',
  'rattrapage': 'bg-orange-500',
  'NORMAL': 'bg-blue-600',
  'RATTRAPAGE': 'bg-orange-500',
  'DEVOIR': 'bg-purple-500',
};

// ─── Main component ──────────────────────────────────────────────────────────

const GenerationCopies: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<'generation' | 'impression'>('generation');

  // Form state (préfixe et nombre persistés dans localStorage)
  const [formData, setFormData] = useState(() => ({
    nombre_copies: parseInt(localStorage.getItem('copies_nombre') || '100', 10) || 100,
    prefixe_code: localStorage.getItem('copies_prefixe') || 'EX',
  }));
  const [continuiteMode, setContinuiteMode] = useState(true);
  const [dernierCodeGenere, setDernierCodeGenere] = useState(0);
  // Fix 2026-08-27 : session (ordinaire/rattrapage) désormais choisie
  // explicitement AVANT génération (avant : parsing fragile du préfixe SR).
  // Persisté dans localStorage pour éviter la re-sélection à chaque ouverture.
  const [typeExamen, setTypeExamen] = useState<'NORMAL' | 'RATTRAPAGE'>(
    (localStorage.getItem('copies_type_examen') as 'NORMAL' | 'RATTRAPAGE') || 'NORMAL'
  );
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [selectedGenSessionId, setSelectedGenSessionId] = useState<number | null>(null);
  const [exportingLotId, setExportingLotId] = useState<number | null>(null);

  // Lots filters
  const [searchLot, setSearchLot] = useState('');
  const [filterType, setFilterType] = useState<TypeFilter>('ALL');
  const [filterStatus, setFilterStatus] = useState<StatusFilter>('ALL');
  const [filterSession, setFilterSession] = useState('ALL');
  const [expandedLotId, setExpandedLotId] = useState<number | null>(null);
  const [groupBy, setGroupBy] = useState<GroupBy>('session');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  // Queries
  const { data: lots, refetch: refetchLots } = useGetLotsCopiesQuery();
  const { data: statsGlobaux } = useGetStatistiquesGlobalesQuery();
  const { data: prochainCodeData, refetch: refetchProchainCode } = useGetProchainCodeQuery({
    prefixe_code: formData.prefixe_code,
  });
  const [genererLot] = useGenererLotCopiesMutation();
  const [exportExcel] = useLazyExportLotExcelQuery();
  const [deleteLot] = useDeleteLotCopiesMutation();
  const [marquerImprime] = useMarquerLotImprimeMutation();
  const [marquerNonImprime] = useMarquerLotNonImprimeMutation();
  const [marquerUtilise] = useMarquerLotUtiliseMutation();
  const [marquerNonUtilise] = useMarquerLotNonUtiliseMutation();
  const { data: modeles = [] } = useGetModelesCopiesQuery();
  const [genererPdfLots] = useGenererPdfLotsMutation();
  const [selectedModeleId, setSelectedModeleId] = useState<number | null>(null);
  const [pdfLoading, setPdfLoading] = useState<'all' | number | null>(null);

  // Estimation des copies
  const { data: sessions = [] } = useGetSessionsQuery({ annee_academique: '2025-2026' });
  const [getEstimation, { isFetching: estimationLoading }] = useLazyGetEstimationCopiesQuery();
  const [selectedSessionId, setSelectedSessionId] = useState<number | null>(null);
  const [estimation, setEstimation] = useState<EstimationCopiesResponse | null>(null);
  const [margePct, setMargePct] = useState(15);
  const [showEstimationDetails, setShowEstimationDetails] = useState(false);

  const handleEstimer = async () => {
    if (!selectedSessionId) return;
    try {
      const result = await getEstimation({ sessionId: selectedSessionId, marge: margePct }).unwrap();
      setEstimation(result);
    } catch {
      setMessage({ type: 'error', text: 'Erreur lors de l\'estimation. Vérifiez que l\'emploi du temps est généré.' });
    }
  };

  useEffect(() => {
    if (typeof prochainCodeData?.dernier_numero === 'number') {
      setDernierCodeGenere(prochainCodeData.dernier_numero);
    }
  }, [prochainCodeData]);

  // Filtered lots
  const filteredLots = useMemo(() => {
    if (!Array.isArray(lots)) return [];
    return lots.filter((lot) => {
      const q = searchLot.toLowerCase();
      const matchSearch =
        !searchLot ||
        lot.code_lot.toLowerCase().includes(q) ||
        (lot.matiere_nom?.toLowerCase() ?? '').includes(q) ||
        (lot.matiere_code?.toLowerCase() ?? '').includes(q) ||
        (lot.cree_par_nom?.toLowerCase() ?? '').includes(q);
      const matchType = filterType === 'ALL' || lot.type_examen === filterType;
      const matchSession =
        filterSession === 'ALL' ||
        (filterSession === 'NONE' && !lot.session) ||
        (filterSession !== 'NONE' && String(lot.session) === filterSession);
      const taux = Number(lot.taux_association || 0);
      const matchStatus =
        filterStatus === 'ALL' ||
        (filterStatus === 'complet' && taux >= 100) ||
        (filterStatus === 'partiel' && taux > 0 && taux < 100) ||
        (filterStatus === 'vide' && taux === 0);
      return matchSearch && matchType && matchSession && matchStatus;
    });
  }, [lots, searchLot, filterType, filterSession, filterStatus]);

  // Groupement par session d'examen
  const groupedLots = useMemo(() => {
    if (groupBy === 'none') return [{ key: 'all', label: '', color: '', lots: filteredLots }];

    const groups: Record<string, { label: string; color: string; sessionType: string; lots: typeof filteredLots }> = {};

    filteredLots.forEach((lot) => {
      const key = lot.session_nom || `${lot.type_examen || 'NORMAL'}_${lot.semestre || 'S1'}`;
      if (!groups[key]) {
        const sessionType = lot.session_type || lot.type_examen || 'NORMAL';
        const label = lot.session_nom || `${lot.type_examen === 'RATTRAPAGE' ? 'Rattrapage' : lot.type_examen === 'DEVOIR' ? 'Devoirs' : 'Session Ordinaire'} — ${lot.semestre || 'S1'}`;
        const color = TYPE_COLORS[sessionType] || 'bg-gray-500';
        groups[key] = { label, color, sessionType, lots: [] };
      }
      groups[key].lots.push(lot);
    });

    return Object.entries(groups).map(([key, g]) => ({
      key, label: g.label, color: g.color, lots: g.lots,
    }));
  }, [filteredLots, groupBy]);

  // Handlers
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'nombre_copies' ? parseInt(value) : value,
    }));
    if (name === 'prefixe_code') {
      localStorage.setItem('copies_prefixe', value);
      setTimeout(() => refetchProchainCode(), 0);
    } else if (name === 'nombre_copies') {
      localStorage.setItem('copies_nombre', value);
    }
  };

  const genererCodesCopies = (nombre: number, depart = 1): string[] =>
    Array.from({ length: nombre }, (_, i) => `${formData.prefixe_code}${depart + i}`);

  const getPreviewCodes = () => {
    const pointDepart = continuiteMode
      ? (prochainCodeData?.dernier_numero ?? dernierCodeGenere) + 1
      : 1;
    return genererCodesCopies(Math.min(5, formData.nombre_copies), pointDepart);
  };

  const handleGenerer = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage(null);
    try {
      const pointDepart = continuiteMode
        ? (prochainCodeData?.dernier_numero ?? dernierCodeGenere) + 1
        : 1;
      // Persister le choix ordinaire/rattrapage (fix 2026-08-27)
      localStorage.setItem('copies_type_examen', typeExamen);
      const result = await genererLot({
        nombre_copies: formData.nombre_copies,
        prefixe_code: formData.prefixe_code,
        point_depart: pointDepart,
        continuite_mode: continuiteMode,
        type_examen: typeExamen,
        ...(selectedGenSessionId ? { session_id: selectedGenSessionId } : {}),
      }).unwrap();

      setMessage({
        type: 'success',
        text: `${result.message}. ${result.nombre_copies} codes générés (${result.codes[0]} → ${result.codes[result.codes.length - 1]})`,
      });
      setDernierCodeGenere(pointDepart + formData.nombre_copies - 1);
      // Conserver le préfixe et le nombre de copies (persistés dans localStorage)
      refetchProchainCode();
      refetchLots();
    } catch (error: any) {
      const data = error?.data;
      setMessage({
        type: 'error',
        text:
          data?.message ||
          data?.detail ||
          (typeof data === 'string' ? data : data ? JSON.stringify(data) : null) ||
          error?.message ||
          'Erreur lors de la génération',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleExportExcel = async (lotId: number, codeLot: string) => {
    setExportingLotId(lotId);
    try {
      const result = await exportExcel(lotId).unwrap();
      const url = window.URL.createObjectURL(result);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${codeLot}_copies.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage({
        type: 'success',
        text: `Fichier ${codeLot}_copies.xlsx téléchargé. Prêt pour publipostage Word.`,
      });
    } catch {
      setMessage({ type: 'error', text: 'Erreur lors du téléchargement du fichier Excel.' });
    } finally {
      setExportingLotId(null);
    }
  };

  const handleDeleteLot = async (lotId: number, codeLot: string, force = false) => {
    if (!force && !window.confirm(`Supprimer le lot "${codeLot}" ? Le système vérifiera d'abord s'il y a des données liées.`)) return;
    try {
      const res = await deleteLot({ lotId, force }).unwrap();
      setMessage({ type: 'success', text: res?.message || `Lot "${codeLot}" supprimé avec succès.` });
      if (expandedLotId === lotId) setExpandedLotId(null);
    } catch (err: any) {
      const data = err?.data;
      if (err?.status === 409 && data) {
        if (data.confirmation_requise) {
          // Copies associées mais pas notées — demander confirmation forcée
          const details = data.details || {};
          const confirmed = window.confirm(
            `⚠️ Attention : ${details.nb_associees} copie(s) sur ${details.nb_total} sont déjà associées à des étudiants.\n\n` +
            `Supprimer ce lot supprimera aussi ces associations.\n\n` +
            `Voulez-vous vraiment continuer ?`
          );
          if (confirmed) {
            handleDeleteLot(lotId, codeLot, true);
          }
        } else {
          // Copies notées — blocage total
          const details = data.details || {};
          setMessage({
            type: 'error',
            text: `Suppression impossible : ${details.nb_notees || 0} copie(s) notée(s), ${details.nb_notes_examen || 0} note(s) d'examen. ${data.action_requise || ''}`,
          });
        }
      } else {
        setMessage({ type: 'error', text: data?.error || `Erreur lors de la suppression du lot "${codeLot}".` });
      }
    }
  };

  const handleMarquerLot = async (lot: any) => {
    // 4 états possibles. Demande à l'utilisateur quelle action effectuer.
    const choix = window.prompt(
      `🏷️ Marquer le lot ${lot.code_lot}\n\n`
      + `État actuel :\n`
      + `  • Imprimé : ${lot.est_imprime ? `OUI (${lot.imprime_par_nom || '?'} le ${lot.date_impression ? new Date(lot.date_impression).toLocaleDateString('fr-FR') : '?'})` : 'NON'}\n`
      + `  • Utilisé : ${lot.est_utilise ? `OUI (${lot.utilise_par_nom || '?'} le ${lot.date_utilisation ? new Date(lot.date_utilisation).toLocaleDateString('fr-FR') : '?'})` : 'NON'}\n`
      + `  • Note site : ${lot.note_site || '(aucune)'}\n\n`
      + `Tapez :\n`
      + `  i = marquer comme IMPRIMÉ (+ note optionnelle)\n`
      + `  u = marquer comme UTILISÉ / DISTRIBUÉ (+ note optionnelle)\n`
      + `  -i = annuler le marquage imprimé\n`
      + `  -u = annuler le marquage utilisé`
    );
    if (!choix) return;
    const action = choix.trim().toLowerCase();
    try {
      if (action === 'i') {
        const note = window.prompt('Note (site, salle, remarque) — optionnel :', lot.note_site || '');
        if (note === null) return;
        await marquerImprime({ lotId: lot.id, note_site: note.trim() }).unwrap();
        setMessage({ type: 'success', text: `Lot ${lot.code_lot} marqué comme imprimé.` });
      } else if (action === 'u') {
        const note = window.prompt('Note (site, salle, remarque) — optionnel :', lot.note_site || '');
        if (note === null) return;
        await marquerUtilise({ lotId: lot.id, note_site: note.trim() }).unwrap();
        setMessage({ type: 'success', text: `Lot ${lot.code_lot} marqué comme utilisé.` });
      } else if (action === '-i') {
        await marquerNonImprime(lot.id).unwrap();
        setMessage({ type: 'success', text: `Marquage "imprimé" annulé pour ${lot.code_lot}.` });
      } else if (action === '-u') {
        await marquerNonUtilise(lot.id).unwrap();
        setMessage({ type: 'success', text: `Marquage "utilisé" annulé pour ${lot.code_lot}.` });
      } else {
        setMessage({ type: 'error', text: `Action inconnue : "${choix}". Tapez i, u, -i ou -u.` });
      }
    } catch (e: any) {
      setMessage({ type: 'error', text: e?.data?.detail || e?.message || 'Erreur' });
    }
  };

  const handleDownloadPdfLots = async (lotIds: number[], label: string) => {
    if (!selectedModeleId) {
      setMessage({ type: 'error', text: 'Sélectionnez un modèle de copie pour générer les PDFs.' });
      return;
    }
    if (lotIds.length === 0) return;
    // Filtrer automatiquement les lots déjà imprimés/utilisés (verrouillés)
    const verrouilles = filteredLots.filter((l: any) => lotIds.includes(l.id) && (l.est_imprime || l.est_utilise));
    if (verrouilles.length > 0) {
      const lotsAutorises = lotIds.filter((id) => !verrouilles.some((v: any) => v.id === id));
      const lignes = verrouilles.slice(0, 5).map((l: any) => {
        const etat = l.est_utilise ? 'utilisé' : 'imprimé';
        const site = l.note_site ? ` — ${l.note_site}` : '';
        return `  • ${l.code_lot} (${etat}${site})`;
      });
      const restants = verrouilles.length > 5 ? `\n  … et ${verrouilles.length - 5} autre(s)` : '';
      if (lotsAutorises.length === 0) {
        setMessage({
          type: 'error',
          text: `Tous les lots demandés (${verrouilles.length}) sont déjà imprimés ou utilisés. Téléchargement bloqué pour éviter les doublons sur les sites d'examen.`,
        });
        return;
      }
      // Lots mixtes : on télécharge uniquement les autorisés, en avertissant
      const ok = window.confirm(
        `${verrouilles.length} lot(s) sur ${lotIds.length} sont déjà imprimés ou utilisés et seront EXCLUS du téléchargement :\n\n`
        + lignes.join('\n') + restants
        + `\n\nSeuls les ${lotsAutorises.length} lot(s) non encore utilisés seront téléchargés.\n\n`
        + `Continuer ?`
      );
      if (!ok) return;
      lotIds = lotsAutorises;
    }
    setPdfLoading(lotIds.length === 1 ? lotIds[0] : 'all');
    try {
      const result = await genererPdfLots({ modeleId: selectedModeleId, lotIds }).unwrap();

      // Vérifier si la réponse est un Blob (succès) ou un objet JSON (erreur)
      if (!(result instanceof Blob)) {
        const errMsg = (result as any)?.error || (result as any)?.detail || 'Le serveur a retourné une erreur.';
        setMessage({ type: 'error', text: errMsg });
        return;
      }

      const contentType = (result as any)._contentType || result.type || '';
      const isPdf = contentType.includes('pdf');
      const url = window.URL.createObjectURL(result);
      const link = document.createElement('a');
      link.href = url;
      link.download = isPdf
        ? `lot_${lotIds[0]}_copies.pdf`
        : `lots_copies_${lotIds.length}lots.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setMessage({ type: 'success', text: `${label} téléchargé avec succès.` });
    } catch (err: any) {
      const detail = err?.data?.error || err?.data?.detail || 'Erreur lors de la génération des PDFs.';
      setMessage({ type: 'error', text: detail });
    } finally {
      setPdfLoading(null);
    }
  };

  const toggleExpand = (id: number) =>
    setExpandedLotId((prev) => (prev === id ? null : id));

  // ─── Render ────────────────────────────────────────────────────────────────

  const pointDepart = continuiteMode
    ? (prochainCodeData?.dernier_numero ?? dernierCodeGenere) + 1
    : 1;

  const activeFilters = (filterType !== 'ALL' ? 1 : 0) + (filterStatus !== 'ALL' ? 1 : 0) + (filterSession !== 'ALL' ? 1 : 0);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-3">
          <Barcode size={32} />
          Copies d'Examens
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Génération des codes-barres et impression des copies d'examens.
        </p>
      </div>

      {/* Onglets */}
      <div className="flex gap-1 mb-6 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('generation')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'generation'
              ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <Barcode size={16} />
          Génération de codes
        </button>
        <button
          onClick={() => setActiveTab('impression')}
          className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
            activeTab === 'impression'
              ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
              : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          <FileImage size={16} />
          Impression des copies
        </button>
      </div>

      {activeTab === 'impression' ? (
        <ImpressionCopies />
      ) : (
      <>
      {/* ── Stats globales ── */}
      {statsGlobaux && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            {
              icon: <FileText size={22} />,
              label: 'Total copies',
              value: statsGlobaux.total_copies,
              repartition: (statsGlobaux as any).repartition?.total_copies,
              bg: 'bg-blue-50 dark:bg-blue-900/20',
              border: 'border-blue-200 dark:border-blue-800/40',
              text: 'text-blue-600 dark:text-blue-400',
              val: 'text-blue-900 dark:text-blue-200',
            },
            {
              icon: <Users size={22} />,
              label: 'Copies associées',
              value: statsGlobaux.copies_associees,
              repartition: (statsGlobaux as any).repartition?.copies_associees,
              bg: 'bg-green-50 dark:bg-green-900/20',
              border: 'border-green-200 dark:border-green-800/40',
              text: 'text-green-600 dark:text-green-400',
              val: 'text-green-900 dark:text-green-200',
            },
            {
              icon: <Award size={22} />,
              label: 'Copies notées',
              value: statsGlobaux.copies_notees,
              repartition: undefined,
              bg: 'bg-purple-50 dark:bg-purple-900/20',
              border: 'border-purple-200 dark:border-purple-800/40',
              text: 'text-purple-600 dark:text-purple-400',
              val: 'text-purple-900 dark:text-purple-200',
            },
            {
              icon: <BarChart2 size={22} />,
              label: 'Lots générés',
              value: statsGlobaux.total_lots,
              repartition: (statsGlobaux as any).repartition?.lots,
              bg: 'bg-orange-50 dark:bg-orange-900/20',
              border: 'border-orange-200 dark:border-orange-800/40',
              text: 'text-orange-600 dark:text-orange-400',
              val: 'text-orange-900 dark:text-orange-200',
            },
          ].map((s) => (
            <div
              key={s.label}
              className={`rounded-xl border p-4 flex flex-col gap-2 ${s.bg} ${s.border}`}
            >
              <div className="flex items-center gap-3">
                <div className={s.text}>{s.icon}</div>
                <div>
                  <p className={`text-xs font-medium ${s.text}`}>{s.label}</p>
                  <p className={`text-2xl font-bold ${s.val}`}>{s.value}</p>
                </div>
              </div>
              {s.repartition && <RepartitionMini data={s.repartition} />}
            </div>
          ))}
        </div>
      )}

      {/* ── Feedback message ── */}
      {message && (
        <div
          className={`mb-6 p-4 rounded-lg flex items-start gap-2 text-sm ${
            message.type === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-800/40'
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-800/40'
          }`}
        >
          {message.type === 'success' ? (
            <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
          ) : (
            <XCircle size={18} className="shrink-0 mt-0.5" />
          )}
          <span>{message.text}</span>
          <button
            onClick={() => setMessage(null)}
            className="ml-auto shrink-0 opacity-60 hover:opacity-100"
          >
            <XCircle size={16} />
          </button>
        </div>
      )}

      {/* ── Estimation des copies ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-100 dark:border-gray-700 p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <Calculator size={20} className="text-indigo-500" />
          Estimation des copies nécessaires
        </h2>

        <div className="flex flex-wrap items-end gap-4 mb-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Session d'examen
            </label>
            <select
              value={selectedSessionId || ''}
              onChange={(e) => { setSelectedSessionId(e.target.value ? +e.target.value : null); setEstimation(null); }}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">-- Choisir une session --</option>
              {sessions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nom} ({s.annee_academique} S{s.semestre})
                </option>
              ))}
            </select>
          </div>
          <div className="w-28">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Marge (%)
            </label>
            <input
              type="number"
              value={margePct}
              onChange={(e) => setMargePct(+e.target.value)}
              min={0}
              max={50}
              step={5}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>
          <button
            onClick={handleEstimer}
            disabled={!selectedSessionId || estimationLoading}
            className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-lg font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {estimationLoading ? <Loader2 size={16} className="animate-spin" /> : <Calculator size={16} />}
            Estimer
          </button>
        </div>

        {estimation && (
          <div className="space-y-4">
            {/* Résumé global */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40 rounded-xl p-4 text-center">
                <p className="text-xs font-medium text-blue-600 dark:text-blue-400">Effectif total</p>
                <p className="text-2xl font-bold text-blue-900 dark:text-blue-200">{estimation.total_effectif}</p>
                <p className="text-[10px] text-blue-500 dark:text-blue-400">étudiants (sans marge)</p>
              </div>
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800/40 rounded-xl p-4 text-center">
                <p className="text-xs font-medium text-indigo-600 dark:text-indigo-400">Copies nécessaires</p>
                <p className="text-2xl font-bold text-indigo-900 dark:text-indigo-200">{estimation.total_copies_avec_marge}</p>
                <p className="text-[10px] text-indigo-500 dark:text-indigo-400">avec marge +{estimation.marge_pourcentage}%</p>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/40 rounded-xl p-4 text-center">
                <p className="text-xs font-medium text-green-600 dark:text-green-400">Créneaux d'examen</p>
                <p className="text-2xl font-bold text-green-900 dark:text-green-200">{estimation.nombre_creneaux}</p>
                <p className="text-[10px] text-green-500 dark:text-green-400">matières planifiées</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/40 rounded-xl p-4 text-center">
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400">Copies de marge</p>
                <p className="text-2xl font-bold text-amber-900 dark:text-amber-200">{estimation.total_copies_avec_marge - estimation.total_effectif}</p>
                <p className="text-[10px] text-amber-500 dark:text-amber-400">copies supplémentaires</p>
              </div>
            </div>

            {/* Par niveau */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {estimation.resume_par_niveau.map((niv) => (
                <div key={niv.niveau} className={`rounded-lg border p-3 ${
                  niv.niveau === 'L1' ? 'bg-blue-50/50 border-blue-200 dark:bg-blue-900/10 dark:border-blue-800/30' :
                  niv.niveau === 'L2' ? 'bg-purple-50/50 border-purple-200 dark:bg-purple-900/10 dark:border-purple-800/30' :
                  'bg-teal-50/50 border-teal-200 dark:bg-teal-900/10 dark:border-teal-800/30'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-bold text-gray-800 dark:text-gray-200">{niv.niveau}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">{niv.nb_creneaux} matières</span>
                  </div>
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="text-lg font-bold text-gray-900 dark:text-gray-100">{niv.copies_avec_marge}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400">copies ({niv.effectif} étudiants)</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Toggle détails */}
            <button
              onClick={() => setShowEstimationDetails(!showEstimationDetails)}
              className="flex items-center gap-1 text-sm text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              {showEstimationDetails ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {showEstimationDetails ? 'Masquer le détail par matière' : 'Voir le détail par matière'}
            </button>

            {/* Tableau détaillé */}
            {showEstimationDetails && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      {['Date', 'Heure', 'Matière', 'Niveau', 'Groupe', 'Effectif', 'Copies (+marge)'].map(h => (
                        <th key={h} className="px-3 py-2 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {estimation.details.map((d) => (
                      <tr key={d.creneau_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                        <td className="px-3 py-1.5 whitespace-nowrap">{new Date(d.date).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short' })}</td>
                        <td className="px-3 py-1.5 whitespace-nowrap font-mono text-xs">{d.heure}</td>
                        <td className="px-3 py-1.5 font-medium">{d.matiere}</td>
                        <td className="px-3 py-1.5">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            d.niveau === 'L1' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' :
                            d.niveau === 'L2' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' :
                            'bg-teal-100 text-teal-700 dark:bg-teal-900/40 dark:text-teal-300'
                          }`}>{d.niveau}</span>
                        </td>
                        <td className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400">{d.groupe}</td>
                        <td className="px-3 py-1.5 text-center">{d.effectif}</td>
                        <td className="px-3 py-1.5 text-center font-bold text-indigo-600 dark:text-indigo-400">{d.copies_avec_marge}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 dark:bg-gray-700/50 font-bold">
                    <tr>
                      <td colSpan={5} className="px-3 py-2 text-right text-sm">TOTAL</td>
                      <td className="px-3 py-2 text-center">{estimation.total_effectif}</td>
                      <td className="px-3 py-2 text-center text-indigo-600 dark:text-indigo-400">{estimation.total_copies_avec_marge}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Main grid: form + preview ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Form */}
        <div className="lg:col-span-2 bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-100 dark:border-gray-700 p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <Printer size={20} className="text-blue-500" />
            Nouveau lot de copies
          </h2>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800/40 rounded-lg p-3 mb-5 flex gap-2">
            <AlertCircle size={15} className="text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700 dark:text-blue-300">
              Génération <strong>indépendante des matières</strong>. La liaison aux matières et
              aux étudiants s'effectue ultérieurement lors de l'association des copies.
            </p>
          </div>

          <form onSubmit={handleGenerer} className="space-y-5">
            {/* Type de session (fix 2026-08-27 : choisi EXPLICITEMENT avant
                génération — plus de parsing fragile du préfixe SR). */}
            <div className={`rounded-lg border-2 p-3 ${
              typeExamen === 'RATTRAPAGE'
                ? 'border-orange-400 bg-orange-50 dark:bg-orange-900/20 dark:border-orange-700'
                : 'border-blue-300 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-700'
            }`}>
              <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-2">
                Type de session <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2 flex-wrap">
                <label className={`flex-1 min-w-[180px] flex items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer transition-colors ${
                  typeExamen === 'NORMAL'
                    ? 'border-blue-500 bg-blue-100 dark:bg-blue-900/40 text-blue-900 dark:text-blue-100 font-semibold'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-blue-400'
                }`}>
                  <input
                    type="radio"
                    name="type_examen"
                    checked={typeExamen === 'NORMAL'}
                    onChange={() => setTypeExamen('NORMAL')}
                    className="h-4 w-4 text-blue-600"
                  />
                  <div>
                    <div className="text-sm">📘 Session ordinaire</div>
                    <div className="text-[10px] font-normal opacity-70">Examens de fin de semestre</div>
                  </div>
                </label>
                <label className={`flex-1 min-w-[180px] flex items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer transition-colors ${
                  typeExamen === 'RATTRAPAGE'
                    ? 'border-orange-500 bg-orange-100 dark:bg-orange-900/40 text-orange-900 dark:text-orange-100 font-semibold'
                    : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:border-orange-400'
                }`}>
                  <input
                    type="radio"
                    name="type_examen"
                    checked={typeExamen === 'RATTRAPAGE'}
                    onChange={() => setTypeExamen('RATTRAPAGE')}
                    className="h-4 w-4 text-orange-600"
                  />
                  <div>
                    <div className="text-sm">🔄 Session de rattrapage</div>
                    <div className="text-[10px] font-normal opacity-70">Reprise pour étudiants en échec</div>
                  </div>
                </label>
              </div>
              <p className="text-[11px] text-gray-600 dark:text-gray-400 mt-2 italic">
                Le type est enregistré sur le lot — plus besoin d'indice dans le préfixe.
                Vous pouvez utiliser le même préfixe (ex. <code className="px-1 bg-gray-100 dark:bg-gray-800 rounded">EX</code>) pour les deux sessions.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Nombre de copies */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Nombre de copies <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="nombre_copies"
                  value={formData.nombre_copies}
                  onChange={handleChange}
                  min="1"
                  max="1000"
                  required
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Prochain code :{' '}
                  <span className="font-mono font-medium text-blue-600 dark:text-blue-400">
                    {formData.prefixe_code}{pointDepart}
                  </span>
                </p>
              </div>

              {/* Préfixe */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Préfixe <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="prefixe_code"
                  value={formData.prefixe_code}
                  onChange={handleChange}
                  required
                  maxLength={5}
                  placeholder="EX"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Ex : EX, CP, EXAM (5 caractères max)
                </p>
              </div>
            </div>

            {/* Mode de génération */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Mode de génération
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                {[
                  {
                    value: true,
                    label: 'Continuité automatique',
                    desc: `Depuis ${formData.prefixe_code}${pointDepart}`,
                  },
                  {
                    value: false,
                    label: 'Nouvelle série',
                    desc: `Depuis ${formData.prefixe_code}1`,
                  },
                ].map((opt) => (
                  <label
                    key={String(opt.value)}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      continuiteMode === opt.value
                        ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-600'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                    }`}
                  >
                    <input
                      type="radio"
                      name="continuite"
                      checked={continuiteMode === opt.value}
                      onChange={() => setContinuiteMode(opt.value)}
                      className="accent-blue-600"
                    />
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        {opt.label}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                        {opt.desc}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Session d'examen */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                Session d'examen
              </label>
              <select
                value={selectedGenSessionId ?? ''}
                onChange={(e) => setSelectedGenSessionId(e.target.value ? Number(e.target.value) : null)}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">— Aucune session —</option>
                {sessions.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.nom}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Rattacher ce lot à une session permet de regrouper les lots par session.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="text-white" />
                  Génération en cours…
                </>
              ) : (
                <>
                  <Printer size={18} />
                  Générer le lot de copies
                </>
              )}
            </button>
          </form>
        </div>

        {/* Preview + instructions */}
        <div className="space-y-4">
          {/* Preview */}
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800/40 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-green-900 dark:text-green-300 mb-3 flex items-center gap-2">
              <TrendingUp size={16} />
              Prévisualisation
            </h3>
            <p className="text-xs text-green-800 dark:text-green-400 mb-2">
              {formData.nombre_copies} codes seront générés :
            </p>
            <div className="flex flex-wrap gap-2 mb-3">
              {getPreviewCodes().map((code, i) => (
                <span
                  key={i}
                  className="px-2 py-1 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300 rounded font-mono text-xs"
                >
                  {code}
                </span>
              ))}
              {formData.nombre_copies > 5 && (
                <span className="px-2 py-1 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-400 rounded text-xs">
                  +{formData.nombre_copies - 5} autres
                </span>
              )}
            </div>
            <div className="text-xs text-green-700 dark:text-green-400 border-t border-green-200 dark:border-green-700/40 pt-3 space-y-0.5">
              <p>
                Mode :{' '}
                <span className="font-medium">
                  {continuiteMode ? 'Continuité' : 'Nouvelle série'}
                </span>
              </p>
              <p>
                Plage :{' '}
                <span className="font-mono font-medium">
                  {formData.prefixe_code}
                  {pointDepart} → {formData.prefixe_code}
                  {pointDepart + formData.nombre_copies - 1}
                </span>
              </p>
            </div>
          </div>

          {/* Instructions */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
              Flux de travail
            </h3>
            <ol className="space-y-2">
              {[
                'Saisissez le nombre de copies et le préfixe',
                'Cliquez sur "Générer"',
                'Téléchargez le fichier Excel du lot',
                'Réalisez le publipostage Word (police code-barres)',
                'Imprimez les copies',
                'Associez les copies aux étudiants après correction',
              ].map((step, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                  <span className="shrink-0 w-4 h-4 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-[10px] font-bold flex items-center justify-center mt-0.5">
                    {i + 1}
                  </span>
                  {step}
                </li>
              ))}
            </ol>
            <p className="mt-3 text-xs text-gray-500 dark:text-gray-500 bg-gray-50 dark:bg-gray-700/50 rounded p-2">
              Police requise : <span className="font-mono">Libre Barcode 39</span>
            </p>
          </div>
        </div>
      </div>

      {/* ── Lots de copies générés ── */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-100 dark:border-gray-700 overflow-hidden">
        {/* Section header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <BarChart2 size={20} className="text-blue-500" />
              Lots de copies générés
              {Array.isArray(lots) && lots.length > 0 && (
                <span className="ml-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                  {filteredLots.length}
                  {filteredLots.length !== lots.length && ` / ${lots.length}`}
                </span>
              )}
            </h2>
          </div>
          <button
            onClick={() => refetchLots()}
            title="Actualiser"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <RefreshCw size={13} />
            Actualiser
          </button>
        </div>

        {/* Filter bar */}
        <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap gap-3 bg-gray-50/60 dark:bg-gray-800/60">
          {/* Search */}
          <div className="relative flex-1 min-w-[180px]">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={searchLot}
              onChange={(e) => setSearchLot(e.target.value)}
              placeholder="Rechercher (code, matière…)"
              className="w-full pl-8 pr-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Session filter */}
          <select
            value={filterSession}
            onChange={(e) => setFilterSession(e.target.value)}
            className="px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">Toutes les sessions</option>
            <option value="NONE">Sans session</option>
            {sessions.map((s) => (
              <option key={s.id} value={String(s.id)}>{s.nom}</option>
            ))}
          </select>

          {/* Type filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as TypeFilter)}
            className="px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">Tous les types</option>
            <option value="NORMAL">Normal</option>
            <option value="RATTRAPAGE">Rattrapage</option>
            <option value="DEVOIR">Devoir</option>
          </select>

          {/* Status filter */}
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as StatusFilter)}
            className="px-3 py-1.5 text-xs border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="ALL">Tous les statuts</option>
            <option value="complet">Complets (100%)</option>
            <option value="partiel">Partiels (&gt;0%)</option>
            <option value="vide">Non associés (0%)</option>
          </select>

          {/* Group by */}
          <button
            onClick={() => setGroupBy(g => g === 'none' ? 'session' : 'none')}
            className={`px-3 py-1.5 text-xs border rounded-lg transition-colors ${
              groupBy !== 'none'
                ? 'bg-indigo-50 dark:bg-indigo-900/30 border-indigo-300 dark:border-indigo-700 text-indigo-700 dark:text-indigo-300 font-medium'
                : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
            title={groupBy !== 'none' ? 'Désactiver le regroupement' : 'Grouper par type + semestre'}
          >
            <Package size={12} className="inline mr-1" />
            {groupBy !== 'none' ? 'Groupé' : 'Grouper'}
          </button>

          {/* Clear filters */}
          {activeFilters > 0 && (
            <button
              onClick={() => {
                setFilterType('ALL');
                setFilterStatus('ALL');
                setFilterSession('ALL');
                setSearchLot('');
              }}
              className="px-3 py-1.5 text-xs text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/40 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              Réinitialiser ({activeFilters})
            </button>
          )}
        </div>

        {/* PDF Download bar */}
        {Array.isArray(lots) && lots.length > 0 && (
          <div className="px-6 py-3 border-b border-gray-100 dark:border-gray-700 flex flex-wrap items-center gap-3 bg-indigo-50/50 dark:bg-indigo-900/10">
            <Package size={16} className="text-indigo-500" />
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Télécharger en PDF :</span>
            <select
              value={selectedModeleId ?? ''}
              onChange={(e) => setSelectedModeleId(e.target.value ? Number(e.target.value) : null)}
              className="px-3 py-1.5 text-xs border border-indigo-200 dark:border-indigo-700 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">— Choisir un modèle —</option>
              {modeles.filter(m => m.est_actif).map((m) => (
                <option key={m.id} value={m.id}>{m.nom}</option>
              ))}
            </select>
            <button
              onClick={() => handleDownloadPdfLots(filteredLots.map(l => l.id), `ZIP de ${filteredLots.length} lot(s)`)}
              disabled={!selectedModeleId || filteredLots.length === 0 || pdfLoading === 'all'}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {pdfLoading === 'all' ? (
                <><Loader2 size={13} className="animate-spin" /> Génération…</>
              ) : (
                <><FileDown size={13} /> Télécharger tous les lots ({filteredLots.length})</>
              )}
            </button>
          </div>
        )}

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="w-8 px-4 py-3" />
                {[
                  { label: 'Code lot', align: 'left' },
                  { label: 'Préfixe', align: 'center' },
                  { label: 'Matière', align: 'left' },
                  { label: 'Type', align: 'left' },
                  { label: 'Créé le', align: 'left' },
                  { label: 'Copies', align: 'center' },
                  { label: 'Association', align: 'left', wide: true },
                  { label: 'Actions', align: 'center' },
                ].map((col) => (
                  <th
                    key={col.label}
                    className={`px-4 py-3 text-${col.align} text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide ${col.wide ? 'min-w-[160px]' : ''}`}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {groupedLots.map((group) => {
                const isGroupCollapsed = collapsedGroups.has(group.key);
                return (
                  <React.Fragment key={group.key}>
                    {/* Group header */}
                    {groupBy !== 'none' && group.label && (
                      <tr
                        className="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700/60"
                        onClick={() => {
                          const s = new Set(collapsedGroups);
                          isGroupCollapsed ? s.delete(group.key) : s.add(group.key);
                          setCollapsedGroups(s);
                        }}
                      >
                        <td colSpan={9} className="px-4 py-2.5">
                          <div className="flex items-center gap-3">
                            {isGroupCollapsed ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronUp size={14} className="text-gray-400" />}
                            <span className={`inline-block w-3 h-3 rounded-full ${group.color}`} />
                            <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">
                              {group.label}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">
                              {group.lots.length} lot(s)
                            </span>
                            <span className="text-xs text-gray-400 dark:text-gray-500">
                              {group.lots.reduce((s, l) => s + l.nombre_copies, 0)} copies
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Group rows */}
                    {(!isGroupCollapsed || groupBy === 'none') && group.lots.map((lot, index) => {
                const taux = Number(lot.taux_association || 0);
                const isExpanded = expandedLotId === lot.id;
                const lotNumero = group.lots.length - index;
                const lotVerrouille = lot.est_imprime || lot.est_utilise;
                return (
                  <React.Fragment key={lot.id}>
                    <tr
                      className={`transition-colors ${
                        isExpanded
                          ? 'bg-blue-50/50 dark:bg-blue-900/10'
                          : lotVerrouille
                          ? (lot.est_utilise
                              ? 'bg-orange-50/40 dark:bg-orange-900/10 hover:bg-orange-50 dark:hover:bg-orange-900/20'
                              : 'bg-amber-50/40 dark:bg-amber-900/10 hover:bg-amber-50 dark:hover:bg-amber-900/20')
                          : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                      }`}
                    >
                      {/* Expand toggle */}
                      <td className="pl-4 pr-2 py-3">
                        <button
                          onClick={() => toggleExpand(lot.id)}
                          title={isExpanded ? 'Masquer les détails' : 'Voir les détails'}
                          className="text-gray-400 hover:text-blue-500 dark:hover:text-blue-400 transition-colors"
                        >
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                      </td>

                      {/* Code lot */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 text-xs font-bold shrink-0">
                            {lotNumero}
                          </span>
                          <span className="font-mono text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {lot.code_lot}
                          </span>
                        </div>
                        {/* Badges Imprimé / Utilisé — bien visibles */}
                        {(lot.est_imprime || lot.est_utilise) && (
                          <div className="mt-1.5 flex flex-wrap gap-1.5 items-center">
                            {lot.est_imprime && (
                              <span
                                title={`Imprimé${lot.imprime_par_nom ? ' par ' + lot.imprime_par_nom : ''}${lot.date_impression ? ' le ' + new Date(lot.date_impression).toLocaleDateString('fr-FR') : ''}${lot.note_site ? ' — ' + lot.note_site : ''}`}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-500 dark:bg-amber-600 text-white border border-amber-600 dark:border-amber-700 rounded-full text-[11px] font-bold shadow-sm"
                              >
                                <Printer size={11} /> Déjà imprimé
                              </span>
                            )}
                            {lot.est_utilise && (
                              <span
                                title={`Utilisé${lot.utilise_par_nom ? ' par ' + lot.utilise_par_nom : ''}${lot.date_utilisation ? ' le ' + new Date(lot.date_utilisation).toLocaleDateString('fr-FR') : ''}${lot.note_site ? ' — ' + lot.note_site : ''}`}
                                className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-600 dark:bg-orange-700 text-white border border-orange-700 dark:border-orange-800 rounded-full text-[11px] font-bold shadow-sm"
                              >
                                <CheckCircle2 size={11} /> Déjà utilisé
                              </span>
                            )}
                            {lot.note_site && (
                              <span className="text-[10px] text-gray-600 dark:text-gray-400 italic truncate max-w-[120px]" title={lot.note_site}>
                                · {lot.note_site}
                              </span>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Préfixe */}
                      <td className="px-4 py-3 text-center">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold font-mono bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300">
                          {lot.prefixe || '—'}
                        </span>
                      </td>

                      {/* Matière + auteur du lot */}
                      <td className="px-4 py-3">
                        {lot.matiere_code && lot.matiere_nom && (
                          <div>
                            <p className="text-sm text-gray-800 dark:text-gray-200">
                              {lot.matiere_nom}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                              {lot.matiere_code}
                            </p>
                          </div>
                        )}
                        {lot.cree_par_nom && (
                          <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1 inline-flex items-center gap-1">
                            <Users size={10} /> Généré par <strong>{lot.cree_par_nom}</strong>
                          </p>
                        )}
                      </td>

                      {/* Type */}
                      <td className="px-4 py-3">
                        <TypeBadge type={lot.type_examen} />
                      </td>

                      {/* Date */}
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <Calendar size={11} />
                          {formatDate(lot.date_creation)}
                        </span>
                      </td>

                      {/* Copies */}
                      <td className="px-4 py-3 text-center">
                        <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                          {lot.nombre_copies_associees}
                          <span className="text-gray-400 dark:text-gray-500">
                            /{lot.nombre_copies}
                          </span>
                        </span>
                      </td>

                      {/* Association progress */}
                      <td className="px-4 py-3 min-w-[160px]">
                        <div className="flex items-center gap-2">
                          <div className="flex-1">
                            <ProgressBar value={taux} />
                          </div>
                          <TauxBadge value={taux} />
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-4 py-3 text-center">
                        {(() => {
                          const verrouille = lot.est_imprime || lot.est_utilise;
                          const raison = lot.est_utilise
                            ? `Lot déjà utilisé${lot.note_site ? ' — ' + lot.note_site : ''}. Démarquez d'abord (bouton 🏷️) pour autoriser un nouveau téléchargement.`
                            : lot.est_imprime
                            ? `Lot déjà imprimé${lot.note_site ? ' — ' + lot.note_site : ''}. Démarquez d'abord (bouton 🏷️) pour autoriser un nouveau téléchargement.`
                            : '';
                          return (
                            <div className="flex items-center justify-center gap-1.5">
                              <button
                                onClick={() => handleDownloadPdfLots([lot.id], `PDF du lot ${lot.code_lot}`)}
                                disabled={!selectedModeleId || pdfLoading === lot.id || verrouille}
                                title={verrouille ? raison : (selectedModeleId ? 'Télécharger en PDF' : 'Sélectionnez un modèle')}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {pdfLoading === lot.id ? (
                                  <><Loader2 size={13} className="animate-spin" /> PDF…</>
                                ) : (
                                  <><FileDown size={13} /> PDF</>
                                )}
                              </button>
                              <button
                                onClick={() => handleExportExcel(lot.id, lot.code_lot)}
                                disabled={exportingLotId === lot.id || verrouille}
                                title={verrouille ? raison : 'Exporter en Excel'}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                {exportingLotId === lot.id ? (
                                  <><Spinner size="sm" className="text-white" /> Excel…</>
                                ) : (
                                  <><Download size={13} /> Excel</>
                                )}
                              </button>
                              <button
                                onClick={() => handleMarquerLot(lot)}
                                title={verrouille
                                  ? "Démarquer ce lot (tapez -i ou -u) pour autoriser un nouveau téléchargement"
                                  : "Marquer comme imprimé / utilisé (site, salle)"}
                                className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-xs transition-colors ${
                                  verrouille
                                    ? 'text-orange-700 dark:text-orange-300 bg-orange-50 dark:bg-orange-900/30 hover:bg-orange-100 dark:hover:bg-orange-900/50'
                                    : 'text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20'
                                }`}
                              >
                                🏷️
                              </button>
                              <button
                                onClick={() => handleDeleteLot(lot.id, lot.code_lot)}
                                title="Supprimer ce lot"
                                className="inline-flex items-center gap-1 px-2 py-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-lg text-xs transition-colors"
                              >
                                <Trash2 size={13} />
                              </button>
                            </div>
                          );
                        })()}
                      </td>
                    </tr>

                    {/* Expanded stats row */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={9} className="p-0">
                          <LotDetailRow lotId={lot.id} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>

          {/* Empty state */}
          {filteredLots.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-gray-500">
              <Barcode size={40} className="mb-3 opacity-30" />
              {!lots || lots.length === 0 ? (
                <>
                  <p className="text-sm font-medium">Aucun lot généré</p>
                  <p className="text-xs mt-1">
                    Utilisez le formulaire ci-dessus pour créer votre premier lot.
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium">Aucun résultat</p>
                  <p className="text-xs mt-1">Modifiez les filtres pour afficher des lots.</p>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      </>
      )}
    </div>
  );
};

export default GenerationCopies;
